import React from 'react';
import { Paper, Box, Typography, Chip, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Edit, Delete, Scoreboard, DragIndicator } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { formatKickoff, teamLabel, hasFinalScore, scoringLabel, emptyWeekMessage } from './gameDisplay';
import { SelectBox, SelectAllBox } from './GamesTable';

// Phone layout for the week's games: one card per game, same props and
// callbacks as GamesTable so GameManagement can swap them by breakpoint.
// Reordering keeps a dedicated drag handle, so touching the rest of a card
// scrolls the page instead of starting a drag.
function GameCards({
  games, teams, week, isLoading, onDragEnd, onEnterScores, onEdit, onDelete, selectedIds, onToggleSelect, onSelectAll,
}) {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress size={28} aria-label="Loading games" />
      </Box>
    );
  }

  if (games.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
        {emptyWeekMessage(week)}
      </Paper>
    );
  }

  return (
    <>
    {/* Cards have no header row, so select-all sits above them. */}
    <Box component="label" sx={{ display: 'inline-flex', alignItems: 'center', mb: 1, ml: 0.5, cursor: 'pointer' }}>
      <SelectAllBox games={games} week={week} selectedIds={selectedIds} onSelectAll={onSelectAll} />
      <Typography variant="body2" color="text.secondary">Select all</Typography>
    </Box>
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="games">
        {(provided) => (
          // Every card carries the same bottom margin (rather than Stack
          // spacing, which skips the first item): dnd measures the dragged
          // card's own margin box, so uneven margins misalign the shift.
          <Box ref={provided.innerRef} {...provided.droppableProps} aria-label={`Week ${week} games`} role="list">
            {games.map((game, index) => {
              const away = teamLabel(teams, game.awayTeam, game.awayRanking);
              const home = teamLabel(teams, game.homeTeam, game.homeRanking);
              return (
                <Draggable key={game.id} draggableId={game.id} index={index}>
                  {(draggable, snapshot) => (
                    <Paper
                      ref={draggable.innerRef}
                      {...draggable.draggableProps}
                      role="listitem"
                      data-testid="game-card"
                      elevation={snapshot.isDragging ? 6 : 1}
                      sx={{
                        p: 1.5, mb: 1.5,
                        outline: selectedIds.has(game.id) ? '2px solid' : 'none',
                        outlineColor: 'primary.main',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Drag to reorder">
                          <Box
                            component="span"
                            aria-label={`Drag to reorder ${teamLabel(teams, game.awayTeam)} at ${teamLabel(teams, game.homeTeam)}`}
                            {...draggable.dragHandleProps}
                            sx={{ display: 'inline-flex', cursor: 'grab', color: 'text.secondary', p: 0.5, ml: -0.5 }}
                          >
                            <DragIndicator />
                          </Box>
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                          {index + 1}
                        </Typography>
                        <Box sx={{ my: -1 }}>
                          <SelectBox game={game} teams={teams} selectedIds={selectedIds} onToggleSelect={onToggleSelect} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                          {formatKickoff(game.startTime)}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={scoringLabel(game)}
                          color={game.isPickScore ? 'primary' : 'default'}
                        />
                      </Box>

                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{away}</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                              {game.neutralSite ? 'vs ' : 'at '}
                            </Box>
                            {home}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {hasFinalScore(game) ? (
                            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{game.awayScore}–{game.homeScore}</Typography>
                          ) : (
                            <Typography variant="caption" color="text.disabled">No score</Typography>
                          )}
                        </Box>
                      </Box>

                      <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                          {game.vegasLine || 'No line'}
                          {game.neutralSite ? ` · Neutral site: ${game.neutralSite}` : ''}
                        </Typography>
                        <Tooltip title="Enter Scores">
                          <IconButton size="small" onClick={() => onEnterScores(game)} color="primary">
                            <Scoreboard fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Game Details">
                          <IconButton size="small" onClick={() => onEdit(game)} color="primary">
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Game">
                          <IconButton size="small" onClick={() => onDelete(game)} color="secondary">
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Paper>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
    </>
  );
}

export default GameCards;
